/**
 * A task-like wrapper around a serial promise queue, modelled loosely on
 * ember-concurrency's `enqueue` task.
 *
 * `enqueueTask(task)` builds an internal queue and returns an object with a
 * `perform` method that forwards its arguments to `task`. Each `perform(...)`
 * runs only after the previous run has settled, so concurrent callers never
 * overlap. The promise returned to a caller settles with that run's own
 * result (errors propagate to the caller), while the internal chain swallows
 * rejections so later runs still execute.
 *
 * Reactive task state (e.g. `isRunning`, `isError` atoms) can be added to the
 * returned object later without changing call sites.
 */
/** The task fn this queue wraps: any async function. */
type AsyncTask = (...args: never[]) => Promise<unknown>;

export interface EnqueuedTask<TTask extends AsyncTask> {
  /** Forwards its arguments to the wrapped task, queued behind the previous run. */
  perform: (...args: Parameters<TTask>) => ReturnType<TTask>;
}

export function enqueueTask<TTask extends AsyncTask>(task: TTask): EnqueuedTask<TTask> {
  let tail: Promise<unknown> = Promise.resolve();

  return {
    perform(...args: Parameters<TTask>) {
      const result = tail.then(() => task(...args));
      tail = result.then(
        () => undefined,
        () => undefined,
      );
      // `result` is `Promise<Awaited<ReturnType<TTask>>>`, which is structurally
      // `ReturnType<TTask>`; assert it so callers get the task's own return type.
      return result as ReturnType<TTask>;
    },
  };
}
